import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { api, fmtBRL, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Trash2, ShoppingCart, Ticket as TicketIcon, X, Loader2 } from "lucide-react";
import PublicNav from "@/components/Layout/PublicNav";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateItem, removeItem, clear, subtotal, count } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [osList, setOsList] = useState([]);
  const [couponInput, setCouponInput] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [applying, setApplying] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("pix");

  useEffect(() => {
    api.get("/os-templates").then(({ data }) => setOsList(data)).catch(() => {});
  }, []);

  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setApplying(true);
    try {
      const { data } = await api.get(`/coupons/validate?code=${encodeURIComponent(code)}`);
      setCoupon(data);
      toast.success(`Cupom ${data.code} aplicado — ${data.discount_pct}% OFF`);
    } catch (e) { toast.error(formatApiError(e)); setCoupon(null); }
    finally { setApplying(false); }
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(""); };

  const discount = coupon ? Math.round(subtotal * coupon.discount_pct) / 100 : 0;
  const total = Math.round((subtotal - discount) * 100) / 100;

  const checkout = async () => {
    if (!user) { nav("/login?next=/cart"); return; }
    if (items.length === 0) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/cart", {
        items: items.map((it) => ({ plan_id: it.plan_id, hostname: it.hostname || undefined, os: it.os })),
        coupon_code: coupon ? coupon.code : undefined,
        payment_method: paymentMethod,
      });
      toast.success(`Pedido criado com ${items.length} VPS! Redirecionando…`);
      clear();
      nav(`/dashboard/invoices/${data.invoice.invoice_id}`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <PublicNav />
      <div className="max-w-6xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-8"><ArrowLeft className="h-3.5 w-3.5" /> voltar</Link>
        <div className="flex items-center gap-3 mb-8">
          <ShoppingCart className="h-7 w-7" />
          <h1 className="font-heading text-4xl font-bold tracking-tighter">Meu carrinho <span className="text-zinc-500 text-lg font-mono">({count})</span></h1>
        </div>

        {items.length === 0 ? (
          <div className="border border-white/10 bg-[#121214] p-16 text-center">
            <ShoppingCart className="h-12 w-12 mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-500 mb-6">Seu carrinho está vazio.</p>
            <Button asChild className="rounded-sm bg-white text-black hover:bg-zinc-200"><Link to="/">Ver planos</Link></Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1fr_360px] gap-8">
            {/* items */}
            <div className="space-y-4">
              {items.map((it, idx) => (
                <div key={it.id} className="border border-white/10 bg-[#121214] p-5" data-testid={`cart-item-${idx}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Item {idx + 1}</div>
                      <div className="font-heading text-xl font-bold">{it.plan_name}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-lg">{fmtBRL(it.price_brl)}</span>
                      <button onClick={() => removeItem(it.id)} className="text-zinc-500 hover:text-red-400" data-testid={`cart-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3 mt-4">
                    <div>
                      <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Hostname (opcional)</label>
                      <Input value={it.hostname} onChange={(e) => updateItem(it.id, { hostname: e.target.value })} placeholder="srv1.exemplo.com" className="rounded-sm bg-[#09090B] border-white/10 font-mono text-sm" />
                    </div>
                    <div>
                      <label className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block">Sistema operacional</label>
                      <select value={it.os} onChange={(e) => updateItem(it.id, { os: e.target.value })} style={{colorScheme: "dark"}} className="w-full rounded-sm bg-[#09090B] border border-white/10 px-3 py-2 font-mono text-sm text-white">
                        {osList.map((o) => (<option key={o.slug} value={o.slug} className="bg-[#09090B] text-white">{o.name}</option>))}
                      </select>
                    </div>
                  </div>
                </div>
              ))}
              <Button variant="outline" onClick={clear} className="rounded-sm border-white/20 hover:bg-red-500 hover:text-white hover:border-red-500" data-testid="cart-clear">Limpar carrinho</Button>
            </div>

            {/* summary */}
            <div className="border border-white/10 bg-[#121214] p-6 h-fit sticky top-24">
              <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-4">// resumo do pedido</div>
              <div className="space-y-2 text-sm mb-6">
                <div className="flex justify-between"><span className="text-zinc-500">Itens</span><span className="font-mono">{count}</span></div>
                <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-mono">{fmtBRL(subtotal)}</span></div>
                {coupon && (<div className="flex justify-between text-emerald-400"><span>Desconto ({coupon.code})</span><span className="font-mono">−{fmtBRL(discount)}</span></div>)}
              </div>

              {!coupon ? (
                <div className="flex gap-2 mb-6">
                  <Input value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCoupon())} placeholder="Cupom" className="rounded-sm bg-[#09090B] border-white/10 font-mono uppercase" data-testid="cart-coupon-input" />
                  <Button variant="outline" onClick={applyCoupon} disabled={applying || !couponInput.trim()} className="rounded-sm border-white/20 hover:bg-white hover:text-black shrink-0" data-testid="cart-coupon-apply">{applying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Aplicar"}</Button>
                </div>
              ) : (
                <div className="flex items-center justify-between border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 mb-6 rounded-sm">
                  <div className="flex items-center gap-2"><TicketIcon className="h-3.5 w-3.5 text-emerald-400" /><span className="font-mono text-xs text-emerald-300">{coupon.code} · {coupon.discount_pct}% OFF</span></div>
                  <button onClick={removeCoupon} className="text-emerald-300 hover:text-white" data-testid="cart-coupon-remove"><X className="h-3.5 w-3.5" /></button>
                </div>
              )}

              <div className="border-t border-white/10 pt-4 mb-6">
                <div className="flex items-baseline justify-between"><span className="text-xs text-zinc-500 font-mono uppercase tracking-widest">Total</span><span className="font-mono text-3xl font-bold">{fmtBRL(total)}</span></div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4" aria-label="Método de pagamento">
                <button type="button" onClick={() => setPaymentMethod("pix")} aria-pressed={paymentMethod === "pix"} className={`p-3 text-sm border rounded-sm ${paymentMethod === "pix" ? "border-emerald-500 text-white" : "border-white/10 text-zinc-400"}`}>PIX</button>
                <button type="button" onClick={() => setPaymentMethod("credit_card")} aria-pressed={paymentMethod === "credit_card"} className={`p-3 text-sm border rounded-sm ${paymentMethod === "credit_card" ? "border-emerald-500 text-white" : "border-white/10 text-zinc-400"}`}>Cartão</button>
              </div>
              <Button onClick={checkout} disabled={submitting || items.length === 0} className="w-full rounded-sm bg-white text-black hover:bg-zinc-200 h-12 btn-sharp" data-testid="cart-checkout">
                {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processando…</> : `Finalizar com ${paymentMethod === "pix" ? "PIX" : "cartão"}`}
              </Button>
              <p className="text-xs text-zinc-500 mt-3 text-center">{paymentMethod === "pix" ? `Pagamento único · 1 QR Code para todos os ${count} itens` : "Cartão ou saldo Mercado Pago · pagamento no site do Mercado Pago"}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
